import { useEffect, useRef, useState } from "react";
import { api, idr } from "../../lib/api";
import BarcodeScanner from "../hris/BarcodeScanner";

/**
 * Kotak pencarian POS TUNGGAL per baris — pengganti dua kotak berurutan
 * (nama produk lalu IMEI) di InputNota.jsx & Sales.jsx (kontrak G2 bagian C).
 * SATU sumber dipakai kedua halaman, bukan disalin dua kali — riwayat
 * ItemNamaField/ItemImeiField yang dulu tersalin persis di dua berkas sudah
 * jadi pelajaran (lihat CLAUDE.md "Duplikasi helper melahirkan penyimpangan").
 *
 * Memanggil SATU endpoint `GET /pos/cari` yang menggabungkan pencarian unit
 * ber-IMEI DAN produk/kit katalog (kontrak G2 bagian A) — kasir tidak perlu
 * tahu lebih dulu apakah barang yang dicari itu unit fisik atau produk biasa.
 *
 * SKU TIDAK PERNAH ditampilkan di layar ini (keputusan owner #8) — dikirim
 * balik ke server lewat `onPatch` sebagai kunci internal saja. Identitas yang
 * dilihat kasir: nama, IMEI (unit), stok, gudang.
 *
 * Memilih hasil ber-`tipe:"unit"` mengisi SATU baris sekaligus: nama, sku
 * (tersembunyi), IMEI, dan harga jual — harga BAWAAN unit dari server
 * (`harga_jual_unit` Price List kalau diisi, kalau tidak harga SKU; brief
 * 22 Sep G1, helper `_shared.harga_bawaan_unit`) — plus gudang dokumen lewat
 * `onWarehouseHint` (satu nota/transaksi = satu gudang, kontrak G1 keputusan
 * owner #4, jadi gudangnya di level dokumen bukan per baris).
 *
 * Memilih `tipe:"produk"`/`"kit"` HANYA mengisi sku+nama+harga (+komponen
 * kalau resep kit aktif). Kalau produknya ber-IMEI, kasir memilih unit
 * spesifiknya lewat pemilih IMEI TERPISAH yang dirender pemanggil setelah
 * `item.sku` terisi — itu masih komponen ItemImeiField lama, memanggil
 * endpoint lama `/inventory/units/pilihan-jual` (TIDAK disentuh kontrak ini).
 *
 * Baris non-stok (kontrak G1 keputusan owner #2, mis. jasa/titipan) tetap
 * bisa diisi bebas — mencentangnya mengunci pencarian, sama seperti perilaku
 * ItemNamaField lama.
 */
export default function ItemCariField({
  index, item, bolehLookupProduk, bolehLookupUnit,
  warehouseId, onWarehouseHint,
  kitResepMap, komponenAwal,
  deriveKode, deriveKategori,
  onPatch,
}) {
  const [saran, setSaran] = useState([]);
  const [mencari, setMencari] = useState(false);
  const debounceRef = useRef(null);
  const reqSeq = useRef(0);
  // Modul izinnya BEDA dari modul unit (inventory_produk vs inventory_unit) —
  // kotak ini tetap boleh mencari selama SALAH SATU diizinkan, endpoint
  // /pos/cari sendiri yang menyaring hasil sesuai izin nyata pengguna
  // (kontrak G2 A.5). Tanpa keduanya, kotak tetap bisa dipakai ketik bebas.
  const bolehCari = bolehLookupProduk || bolehLookupUnit;

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const jalankanPencarian = async (q) => {
    const seq = ++reqSeq.current;
    setMencari(true);
    try {
      const { data } = await api.get("/pos/cari", { params: { q, warehouse_id: warehouseId || undefined } });
      if (seq === reqSeq.current) setSaran(data.items || []);
    } catch {
      // Diam-diam nonaktif — modul lookup mungkin tidak diizinkan (403) atau
      // jaringan bermasalah. Baris tetap bisa disimpan lewat ketik bebas.
      if (seq === reqSeq.current) setSaran([]);
    } finally {
      if (seq === reqSeq.current) setMencari(false);
    }
  };

  const cari = (teks) => {
    onPatch({ nama_barang: teks, kode: deriveKode(teks), kategori: deriveKategori(teks) });
    // Baris non-stok (kontrak G1 keputusan owner #2) sengaja TIDAK mencari
    // saran katalog — menandainya berarti "sengaja tidak menyentuh stok",
    // jadi pencarian yang bisa menempelkan SKU harus terkunci.
    if (!bolehCari || item.non_stok) { setSaran([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = teks.trim();
    if (q.length < 2) { setSaran([]); return; }
    debounceRef.current = setTimeout(() => jalankanPencarian(q), 300);
  };

  // Hasil scan kamera dicari LANGSUNG tanpa jeda debounce — kasir sudah
  // menunjuk barang yang tepat lewat kamera, menunggu 300ms lagi cuma
  // terasa lambat. Komponen scanner: components/hris/BarcodeScanner.jsx
  // (SUDAH ADA, dipakai 4 layar lain — TIDAK disalin di sini).
  const dariScan = (kode) => {
    onPatch({ nama_barang: kode, kode: deriveKode(kode), kategori: deriveKategori(kode) });
    if (!bolehCari || item.non_stok) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    jalankanPencarian(kode.trim());
  };

  const pilih = (r) => {
    if (r.tipe === "unit") {
      onPatch({
        nama_barang: r.nama || r.label,
        sku: r.sku, // kunci internal — tidak pernah dirender
        imei: r.imei || "",
        _requiresImei: true,
        harga: r.harga_jual || 0,
        kode: deriveKode(r.nama || r.label),
        kategori: r.kategori || deriveKategori(r.nama || r.label),
        komponen: undefined, // unit tunggal tidak pernah bawa resep kit
        non_stok: false,
      });
      // Satu nota/transaksi = satu gudang (kontrak G1 #4) — unit yang
      // ditemukan sudah menunjukkan gudang aslinya, jadi diisikan otomatis
      // kalau dokumen belum punya gudang terpilih (lihat docstring pemanggil).
      if (onWarehouseHint && r.warehouse_id) onWarehouseHint(r.warehouse_id);
    } else {
      const resepKit = kitResepMap?.[r.sku];
      onPatch({
        nama_barang: r.nama || r.label,
        sku: r.sku,
        harga: r.harga_jual || 0,
        kode: deriveKode(r.nama || r.label),
        kategori: r.kategori || deriveKategori(r.nama || r.label),
        komponen: resepKit && komponenAwal ? komponenAwal(resepKit) : undefined,
        // Ganti pilihan dari unit ke produk katalog biasa HARUS melepas IMEI
        // lama — unit yang tadinya terpilih belum tentu cocok dengan produk
        // baru ini (baris ini sengaja SELALU menimpa, bukan cuma saat kosong).
        imei: "",
        _requiresImei: r.tipe !== "kit" && !!r.has_imei,
        // Memilih dari katalog otomatis mematikan non-stok (kontrak G1) —
        // baris ber-SKU tidak boleh ditandai non-stok, backend menolak 400
        // kalau keduanya bersamaan.
        non_stok: false,
      });
    }
    setSaran([]);
  };

  return (
    <div className="relative">
      {item._requiresImei && !item.imei && !item.non_stok && <p className="mb-1 text-xs font-medium text-rose-600" role="status">Pilih atau scan IMEI/SN di bawah. Tipe produk saja belum memilih unit yang dijual.</p>}
      <div className="flex items-center gap-1.5">
        <input value={item.nama_barang} onChange={e => cari(e.target.value)}
          placeholder="Cari nama / IMEI / kode barang…" data-testid={`item-cari-${index}`}
          className="h-9 px-2 rounded border border-slate-200 text-xs w-full" />
        <BarcodeScanner onDetected={dariScan} testId={`item-scan-${index}`}
          title="Scan IMEI / barcode barang" />
      </div>
      {/* Baris non-stok eksplisit (kontrak G1 keputusan owner #2) — HANYA
          relevan sebelum baris tertaut katalog. Mencentangnya mengunci
          pencarian (IMEI ikut tidak pernah muncul karena pemilihnya
          digerbang `item.sku` di form induk). */}
      {!item.sku && (
        <label className="mt-1 flex items-center gap-1.5 text-[10px] text-slate-500 cursor-pointer"
          title="Centang kalau baris ini JASA/BARANG DI LUAR STOK (mis. servis, titipan) — tidak akan mengurangi stok gudang.">
          <input type="checkbox" checked={!!item.non_stok}
            onChange={e => onPatch({ non_stok: e.target.checked })}
            data-testid={`item-nonstok-${index}`} className="w-3 h-3 accent-emerald-600" />
          Non-stok (jasa/barang luar)
        </label>
      )}
      {item.sku && (
        <span data-testid={`item-sku-badge-${index}`}
          className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">
          {/* SKU TIDAK PERNAH ditampilkan (keputusan owner #8, kontrak G2) —
              badge ini cuma menandakan baris sudah tertaut katalog internal;
              kode aslinya tetap tersimpan di item.sku untuk dikirim server. */}
          Terhubung katalog
          {/* Melepas tautan ikut melepas IMEI yang sudah dipilih — IMEI itu
              cuma sah selama masih tertaut katalog. */}
          <button type="button" onClick={() => onPatch({ sku: "", imei: "", komponen: undefined })}
            title="Lepas tautan katalog — kembali ke ketik bebas"
            className="text-emerald-600 hover:text-emerald-900 leading-none">×</button>
        </span>
      )}
      {item.sku && item.komponen?.length > 0 && (
        <span data-testid={`item-kit-badge-${index}`}
          className="mt-1 ml-1 inline-flex items-center gap-1 text-[10px] bg-sky-50 text-sky-700 border border-sky-200 rounded px-1.5 py-0.5">
          Paket kit · {item.komponen.length} komponen
        </span>
      )}
      {saran.length > 0 && (
        <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto"
          data-testid={`item-cari-hasil-${index}`}>
          {/* Stok habis TIDAK bisa dipilih (keputusan bos, ditegakkan juga di
              backend). Unit ber-IMEI selalu satu-satunya dari statusnya
              sendiri ("tersedia"), jadi tidak pernah didisable di sini. */}
          {saran.map((r, n) => {
            const habis = r.tipe !== "unit" && !(r.stok_tersedia > 0);
            return (
              <button key={`${r.tipe}-${r.sku || ""}-${r.imei || n}`} type="button" disabled={habis}
                onClick={() => { if (!habis) pilih(r); }}
                title={habis ? (r.keterangan_stok || "Stok kosong di gudang yang dipilih — periksa Inventory") : ""}
                data-testid={`item-saran-${index}-${n}`}
                className={`w-full text-left px-3 py-2 text-xs border-b border-slate-50 last:border-0 ${
                  habis ? "bg-slate-50 opacity-60 cursor-not-allowed" : "hover:bg-emerald-50"}`}>
                <span className="font-medium text-slate-900">{r.nama || r.label}</span>
                {r.tipe === "unit" && r.imei && (
                  <span className="font-mono text-[10px] text-violet-600 ml-1.5">IMEI {r.imei}</span>
                )}
                <span className="text-[10px] text-slate-500 ml-1.5">{r.harga_jual > 0 ? idr(r.harga_jual) : "Harga belum diisi"}</span>
                {/* Harga BAWAAN unit (brief 22 Sep G1) = harga jual unit dari Price
                    List kalau diisi — ditandai supaya kasir tahu kenapa harga IMEI
                    ini beda dari harga produknya. Tetap bisa diubah di baris nota. */}
                {r.tipe === "unit" && r.harga_unit_khusus && (
                  <span className="text-[10px] text-violet-600 ml-1 font-medium"
                    data-testid={`item-saran-harga-unit-${index}-${n}`}>harga unit</span>
                )}
                {/* `tipe:"kit"` = produk paket berresep aktif (pos_cari, brief 22 Sep F). */}
                {r.tipe === "kit" && (
                  <span className="text-[10px] text-sky-600 ml-1.5 font-medium">paket</span>
                )}
                <span className={`text-[10px] ml-1.5 font-medium ${habis ? "text-rose-600" : "text-emerald-600"}`}>
                  {habis ? (r.keterangan_stok || "stok kosong di gudang ini") : `Ready ${r.stok_tersedia ?? (r.tipe === "unit" ? 1 : 0)}`}
                </span>
                {r.warehouse_nama && (
                  <span className="text-[10px] text-slate-400 ml-1.5">· {r.warehouse_nama}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
      {!mencari && saran.length === 0 && !item.sku && item.nama_barang.trim().length >= 2 && !item.non_stok && (
        <p className="text-[10px] text-slate-400 mt-1">Tidak ada hasil dari katalog — tetap bisa disimpan ketik bebas.</p>
      )}
    </div>
  );
}
